import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import { includes, isNil } from 'ramda';
import React, { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { exists } from '@utils/general';

import { getAuthToken } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import MedicationDeliveryStep, {
	IMedicationDeliveryStepPageContent,
} from './MedicationDeliveryStep.component';
import { dataConstants } from './MedicationDeliveryStep.data';

interface IMedicationDeliveryStepPageProps extends IFlowStepProps {}

const CardSkeleton = styled(Skeleton)`
	height: 24rem;
`;

const MedicationDeliveryStepPage: FC<IMedicationDeliveryStepPageProps> = ({
	path,
	stepKey,
	...props
}) => {
	const [deliveryMethods, setDeliveryMethods] = useState<string[] | null>(null);
	const [deliveryLoading, setDeliveryLoading] = useState<boolean | null>(null);

	const { destinationFlow, existingFormData } = useContext(FlowContext);

	const getDeliveryMethods = async () => {
		const authorizationToken = await getAuthToken();

		try {
			setDeliveryLoading(true);
			const response = await axios.get(
				`${ApiEndpoints.memberPortal}/available-rx-delivery-methods/`,
				{
					headers: {
						authorizationToken,
					},
					responseType: 'json',
				}
			);
			setDeliveryMethods(response.data);
		} catch (error: unknown) {
			setDeliveryMethods([]);
		} finally {
			setDeliveryLoading(false);
		}
	};

	useEffect(() => {
		getDeliveryMethods();
	}, []);

	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "medication-delivery-step" }) {
				heading
				sub_heading
				alternate_page_info
				capsule_disclaimer
				capsule_info_link_text
				capsule_info_link_url
				insurance_accepted
				folx_shipping {
					shipping_header
					shipping_description
					shipping_price
					shipping_price_interval
					enabled
				}
				capsule_delivery {
					capsule_header
					capsule_description
					membership_price
					membership_price_interval
					copay_price
					copay_price_description
					enabled
					capsule_logo
				}
				preferred_pharmacy {
					pharmacy_header
					pharmacy_description
				}
			}
		}
	`);

	const refinedContent: IMedicationDeliveryStepPageContent = keysToCamel(
		pageContent
	) as IMedicationDeliveryStepPageContent;

	const loading = !!deliveryLoading;

	const skeleton = (
		<CenteredGridItem>
			<FlexColumn gap="lg">
				<Skeleton borderRadius="md" gap="md" height="xxl" fullWidth />
				<CardSkeleton
					borderRadius="md"
					structure="column"
					division="equal"
					gap="md"
					height="xxxxl"
					repeated={3}
					fullWidth
				/>
			</FlexColumn>
		</CenteredGridItem>
	);

	return (
		<LoadingWrapper loading={loading} skeleton={skeleton}>
			<MedicationDeliveryStep
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				showCapsuleOption={
					exists(deliveryMethods) && includes(dataConstants.CAPSULE_METHOD, deliveryMethods ?? [])
				}
			/>
		</LoadingWrapper>
	);
};

export default MedicationDeliveryStepPage;
